import { Avatar, Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import { AtsConversationMessageFragment } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUITime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { AttachedFile } from 'modules/ats/components/AttachedFile'
import { isDeclinedOfferTopic, isSentOfferTopic } from 'modules/ats/lib/utils/messages'

import { MessageStatus } from './MessageStatus'

const ACCEPTED_FORMATS = [
  'image/png',
  'image/apng',
  'image/avif',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
  'application/pdf',
  'application/docx',
  'audio/wave',
  'audio/wav',
  'audio/x-wav',
  'audio/x-pn-wav',
  'audio/webm',
  'video/webm',
  'audio/ogg',
  'video/ogg',
  'video/mp4',
  'application/mp4',
  'application/ogg',
  'application/doc',
  'application/ms-doc',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

interface Props {
  message: AtsConversationMessageFragment
  sender: Sender
}

interface Sender {
  fullName: string
  avatarUrl: string | null
}

const getTopicHeader = (message: AtsConversationMessageFragment, senderName: string) => {
  if (isSentOfferTopic(message)) {
    return i18n.t('ats.candidates.messages.topics.header.offer_sent', { senderName: senderName })
  }

  if (isDeclinedOfferTopic(message)) {
    return i18n.t('ats.candidates.messages.topics.header.offer_declined', {
      senderName: senderName,
    })
  }

  return ''
}

export const MessageCard = ({ message, sender }: Props) => {
  const allowedAttachments = message.attachments.filter(({ contentType }) =>
    ACCEPTED_FORMATS.includes(contentType)
  )

  const header = getTopicHeader(message, sender.fullName)
  // TODO: move types to backend
  const isCandidate = message.sentByType.includes('Candidate')

  return (
    <Box
      width="full"
      component="li"
      gap="s8"
      style={{ maxWidth: '95%' }}
      alignSelf={isCandidate ? 'flexStart' : 'flexEnd'}
      alignItems={isCandidate ? 'flexStart' : 'flexEnd'}
    >
      <Box
        gap="s8"
        background={isCandidate ? 'grey200' : 'viridian100'}
        padding="s16"
        borderRadius={{ all: 'abs008' }}
        width="fit-content"
      >
        <Text.Normal overflowWrap>
          <Box width="full" gap="s8">
            {header && <Text.Normal weight="semibold">{header}</Text.Normal>}
            <RichTextDisplay content={message.content} />
          </Box>
        </Text.Normal>
        {allowedAttachments.length > 0 && (
          <Box paddingY="s8" gap="s16" width="s360">
            {allowedAttachments.map((attachment, index) => (
              <AttachedFile key={index} attachment={attachment} />
            ))}
          </Box>
        )}
      </Box>

      <Box gap="s8" flexDirection={isCandidate ? 'row' : 'rowReverse'} alignItems="center">
        <Avatar url={buildUrl(sender.avatarUrl)} label={sender.fullName} size="s24" />
        <Box flexDirection={isCandidate ? 'row' : 'rowReverse'} alignItems="center" gap="s6">
          <Text.Normal color="grey800" size="200" weight="medium">
            {sender.fullName}
          </Text.Normal>{' '}
          <Text.Normal color="grey800" size="200">
            {dateBackendDateTimeToUITime(message.createdAt)}
          </Text.Normal>
          {!isCandidate && <MessageStatus message={message} />}
        </Box>
      </Box>
    </Box>
  )
}
